import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Calendar, Users, FileText, Send, Bell, Gift, MessageCircle, Building2,
  ArrowRight, CheckCircle2, Clock, Heart, MapPin, Star, Sparkles, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierConfig = {
  bronze: { label: 'Bronze Member', color: 'from-amber-700 to-amber-500', bg: 'bg-amber-50', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  silver: { label: 'Silver Member', color: 'from-slate-400 to-slate-300', bg: 'bg-slate-50', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  gold: { label: 'Gold Member', color: 'from-yellow-500 to-amber-400', bg: 'bg-yellow-50', text: 'text-yellow-800', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

// Compute tier display from real membership plan (preferred) or legacy tier, or fallbacks
const resolveMembership = (dashboardUser) => {
  const plan = dashboardUser?.membership_plan;
  if (plan && plan.name) {
    const c = plan.color || '#002249';
    return {
      label: `${plan.name} Member`,
      gradient: `linear-gradient(135deg, ${c}, ${c}dd)`,
      chipBg: 'bg-white/90',
      chipText: 'text-[#002249]',
      chipBorder: 'border-white/60',
      price: plan.price,
    };
  }
  if (dashboardUser?.leadership_level) {
    return {
      label: dashboardUser.leadership_title || 'Leadership',
      gradient: 'linear-gradient(135deg, #002249, #0a3570)',
      chipBg: 'bg-amber-100',
      chipText: 'text-amber-800',
      chipBorder: 'border-amber-200',
    };
  }
  const legacy = tierConfig[dashboardUser?.membership_tier];
  if (legacy) {
    return {
      label: legacy.label,
      gradient: null, // fallback to tailwind class via tier.color
      legacyColor: legacy.color,
      chipBg: 'bg-white/90 ' + legacy.badge,
      chipText: '',
      chipBorder: '',
    };
  }
  return {
    label: dashboardUser?.is_founding_member ? 'Founding Member' : 'Member',
    gradient: 'linear-gradient(135deg, #002249, #0a3570)',
    chipBg: 'bg-white/90',
    chipText: 'text-[#002249]',
    chipBorder: '',
  };
};

const PortalDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);  

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/portal/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full" />
      </div>
    );
  }

  const tier = tierConfig[dashboard?.user?.membership_tier] || tierConfig.bronze;
  const membership = resolveMembership(dashboard?.user);

  const quickLinks = [
    { name: 'Community Feed', icon: MessageCircle, path: '/portal', color: 'bg-teal-500', desc: 'Join the conversation' },
    { name: 'Events', icon: Calendar, path: '/portal/events', color: 'bg-blue-500', desc: 'Find events' },
    { name: 'Committees', icon: Users, path: '/portal/committees', color: 'bg-purple-500', desc: 'Get involved' },
    { name: 'Submit Blog', icon: FileText, path: '/portal/blog', color: 'bg-indigo-500', desc: 'Share your story' },
    { name: 'Company Profile', icon: Building2, path: '/portal/profile', color: 'bg-orange-500', desc: 'Update your info' },
    { name: 'Requests', icon: Send, path: '/portal/requests', color: 'bg-rose-500', desc: 'Submit a request' },
  ];

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="portal-dashboard">
      {/* Welcome Banner — Social, warm */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6"
        data-testid="welcome-banner"
      >
        <div className="bg-gradient-to-r from-[#E63946] via-[#C62D3A] to-[#1D3557] px-6 py-5 sm:px-8 sm:py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${membership.chipBg} ${membership.chipText} ${membership.chipBorder}`} data-testid="dashboard-membership-chip">
                  <Award className="w-3 h-3 inline mr-1" />{membership.label}
                </span>
                {dashboard?.user?.is_founding_member && (
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    <Star className="w-3 h-3 inline mr-1 fill-amber-500 text-amber-500" />Founding Member
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                Hey, {user?.name?.split(' ')[0] || 'Member'}!
              </h1>
              <p className="text-white/80 text-sm">Welcome to your chamber community. Here's what's happening today.</p>
            </div>
            <div className="hidden sm:flex items-center space-x-3">
              <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center text-white font-bold text-2xl">
                  {user?.name?.charAt(0).toUpperCase() || 'M'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip — Clean, easy to scan */}
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100">
          <Link to="/portal/profile" className="p-4 text-center hover:bg-slate-50 transition-colors" data-testid="stat-profile">
            <p className="text-2xl font-bold text-[#002249]">{dashboard?.profile_completion || 0}%</p>
            <p className="text-xs text-slate-500 mt-0.5">Profile Complete</p>
            <Progress value={dashboard?.profile_completion || 0} className="h-1 mt-2" />
          </Link>
          <Link to="/portal/committees" className="p-4 text-center hover:bg-slate-50 transition-colors" data-testid="stat-committees">
            <p className="text-2xl font-bold text-purple-600">{dashboard?.committees_joined || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Committees</p>
          </Link>
          <Link to="/portal/requests" className="p-4 text-center hover:bg-slate-50 transition-colors" data-testid="stat-requests">
            <p className="text-2xl font-bold text-orange-600">{dashboard?.pending_requests || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Pending Requests</p>
          </Link>
          <Link to="/portal/support" className="p-4 text-center hover:bg-slate-50 transition-colors" data-testid="stat-messages">
            <p className="text-2xl font-bold text-[#E63946]">{dashboard?.unread_messages || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Messages</p>
          </Link>
        </div>
      </motion.div>

      {/* Quick Links — Social grid, like app shortcuts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {quickLinks.map((link, i) => (
            <Link key={link.path} to={link.path}>
              <motion.div
                whileHover={{ y: -2 }}
                className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:shadow-md hover:border-slate-300 transition-all cursor-pointer"
                data-testid={`quick-link-${i}`}
              >
                <div className={`w-11 h-11 ${link.color} rounded-xl flex items-center justify-center mx-auto mb-2.5 shadow-sm`}>
                  <link.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-xs font-semibold text-slate-800 leading-tight">{link.name}</p>
                <p className="text-[10px] text-slate-400 mt-0.5 hidden sm:block">{link.desc}</p>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Main Feed — Left 3 cols */}
        <div className="lg:col-span-3 space-y-5">
          {/* Announcements / Chamber Highlights */}
          {dashboard?.announcements?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-r from-[#002249] to-[#457B9D] rounded-2xl p-5 text-white"
              data-testid="chamber-highlights"
            >
              <div className="flex items-center space-x-2 mb-3">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <h3 className="font-bold text-sm">Chamber Highlights</h3>
              </div>
              {dashboard.announcements.slice(0, 2).map((a, i) => (
                <div key={a.id || i} className={`${i > 0 ? 'mt-3 pt-3 border-t border-white/10' : ''}`}>
                  <h4 className="font-semibold text-sm">{a.title}</h4>
                  <p className="text-white/70 text-xs mt-0.5 line-clamp-2">{a.content}</p>
                </div>
              ))}
            </motion.div>
          )}

          {/* Upcoming Events — Card style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-200"
            data-testid="upcoming-events"
          >
            <div className="p-5 pb-3 flex justify-between items-center">
              <h3 className="font-bold text-[#002249]">Upcoming Events</h3>
              <Link to="/portal/events" className="text-xs font-medium text-[#E63946] hover:underline flex items-center">
                See all <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
            <div className="px-5 pb-5">
              {dashboard?.upcoming_events?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.upcoming_events.slice(0, 3).map((event, index) => (
                    <Link to="/portal/events" key={event.id || index}>
                      <div className="flex items-center space-x-3 p-3 rounded-xl hover:bg-slate-50 transition-colors -mx-1" data-testid={`event-${index}`}>
                        <div className="w-14 h-14 bg-[#002249] rounded-xl flex flex-col items-center justify-center flex-shrink-0 text-white">
                          <span className="text-lg font-bold leading-none">{event.date?.split('-')[2]}</span>
                          <span className="text-[10px] text-white/60 uppercase">{new Date(event.date + 'T00:00:00').toLocaleString('en', { month: 'short' })}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-slate-900 truncate">{event.title}</h4>
                          <div className="flex items-center space-x-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{event.time}</span>
                            <span className="flex items-center"><MapPin className="w-3 h-3 mr-1" />{event.location}</span>
                          </div>
                        </div>
                        {event.is_featured && <Star className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" />}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                  <p className="text-sm">No upcoming events right now</p>
                  <Link to="/portal/events" className="text-xs text-[#E63946] mt-1 inline-block hover:underline">Browse event calendar</Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Community Preview — Social feel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl border border-slate-200"
            data-testid="community-preview"
          >
            <div className="p-5 pb-3 flex justify-between items-center">
              <h3 className="font-bold text-[#002249] flex items-center">
                <MessageCircle className="w-4 h-4 mr-2 text-teal-500" /> Community Feed
              </h3>
              <Link to="/portal" className="text-xs font-medium text-[#E63946] hover:underline flex items-center">
                Join conversation <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
            <div className="px-5 pb-5">
              {dashboard?.community_preview?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.community_preview.slice(0, 3).map((post, i) => (
                    <div key={post.id || i} className="flex items-start space-x-3" data-testid={`community-post-${i}`}>
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        {post.user_id?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 line-clamp-2">{post.content}</p>
                        <div className="flex items-center space-x-3 mt-1 text-xs text-slate-400">
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                          <span className="flex items-center"><Heart className="w-3 h-3 mr-0.5" />{post.likes_count || 0}</span>
                          <span className="flex items-center"><MessageCircle className="w-3 h-3 mr-0.5" />{post.comments_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                  <p className="text-sm">Be the first to post something!</p>
                  <Link to="/portal"><Button size="sm" className="mt-2 bg-teal-500 hover:bg-teal-600 rounded-full text-xs px-4">Start a post</Button></Link>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar — 2 cols */}
        <div className="lg:col-span-2 space-y-5">
          {/* Your Membership Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            data-testid="membership-card"
          >
            <div
              className={`p-4 text-white ${membership.gradient ? '' : `bg-gradient-to-r ${membership.legacyColor || tier.color}`}`}
              style={membership.gradient ? { backgroundImage: membership.gradient } : undefined}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/70">Membership</p>
                  <h3 className="font-bold flex items-center mt-0.5">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />Active
                  </h3>
                </div>
                <Award className="w-8 h-8 text-white/30" />
              </div>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-500 mb-2">You're a <strong className="text-slate-800" data-testid="dashboard-membership-label">{membership.label}</strong> of the Dominican Chamber of Commerce of Florida.</p>
              <Link to="/portal/benefits">
                <Button size="sm" variant="outline" className="w-full rounded-lg text-xs" data-testid="view-benefits-btn">
                  View My Benefits <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Blog Submissions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl border border-slate-200"
            data-testid="blog-submissions"
          >
            <div className="p-4 pb-2 flex justify-between items-center">
              <h3 className="font-bold text-sm text-[#002249]">Your Blogs</h3>
              <Link to="/portal/blog" className="text-xs text-[#E63946] font-medium hover:underline">Write new</Link>
            </div>
            <div className="px-4 pb-4">
              {dashboard?.blog_submissions?.length > 0 ? dashboard.blog_submissions.slice(0, 3).map((sub, i) => (
                <div key={sub.id || i} className="py-2.5 border-b border-slate-50 last:border-0" data-testid={`submission-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-800 truncate flex-1 mr-2">{sub.title}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                      sub.status === 'declined' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {sub.status}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-4 text-slate-400">
                  <FileText className="w-8 h-8 mx-auto mb-1 text-slate-200" />
                  <p className="text-xs">No blog submissions yet</p>
                  <Link to="/portal/blog"><Button size="sm" variant="outline" className="mt-2 rounded-full text-xs">Write your first blog</Button></Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl border border-slate-200 p-4"
            data-testid="quick-actions"
          >
            <h3 className="font-bold text-sm text-[#002249] mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Link to="/portal/requests" className="flex items-center space-x-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                <Send className="w-4 h-4 text-green-500" />
                <span className="text-sm text-slate-700">Request a Promotion</span>
              </Link>
              <Link to="/portal/social" className="flex items-center space-x-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                <Gift className="w-4 h-4 text-pink-500" />
                <span className="text-sm text-slate-700">Social Media Request</span>
              </Link>
              <Link to="/portal/support" className="flex items-center space-x-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                <Bell className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-slate-700">Contact Membership Team</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PortalDashboard;
